import { useEffect, useState } from 'react';
import { PaymentError, PaymentErrorCode, reportError } from '../utils/errors';
import { LoadingState } from './LoadingState';
import type {
  GatewayManifestEntry,
  GatewayReturnPayload,
  OrderContext,
  PaymentGatewayAdapter,
} from '../types';

interface GatewayFactoryProps {
  entry: GatewayManifestEntry;
  order: OrderContext;
  onSettled: (payload: GatewayReturnPayload) => void;
  onError: (error: PaymentError) => void;
}

/**
 * Resolves a manifest entry to its adapter (code-split — nothing about a
 * gateway loads until it's picked) and either mounts its inline checkout UI
 * or, for a hosted-redirect gateway, calls `initiate()` itself and navigates
 * to the result. See the plan's §3 — this is the one place that stands
 * between the registry and any gateway's actual code.
 */
export function GatewayFactory({
  entry,
  order,
  onSettled,
  onError,
}: GatewayFactoryProps) {
  const [adapter, setAdapter] = useState<PaymentGatewayAdapter | null>(null);

  useEffect(() => {
    let cancelled = false;

    entry
      .load()
      .then((mod) => {
        if (!cancelled) setAdapter(mod.default);
      })
      .catch((error) => {
        reportError(error, { gatewayId: entry.id, orderId: order.orderId });
        onError(
          new PaymentError(
            PaymentErrorCode.CONFIG_ERROR,
            `Could not load ${entry.label}.`,
            {
              gatewayId: entry.id,
              orderId: order.orderId,
            }
          )
        );
      });

    return () => {
      cancelled = true;
    };
  }, [entry, order.orderId, onError]);

  useEffect(() => {
    if (!adapter || adapter.render) return; // render-based adapters run their own initiate()

    let cancelled = false;

    adapter
      .initiate(order)
      .then((result) => {
        if (cancelled) return;

        if (result.success && result.hostedCheckoutUrl) {
          window.location.href = result.hostedCheckoutUrl;
          return;
        }

        // The backend's own reason for refusing is the only clue to why a
        // hosted checkout never opened — it used to be dropped on the floor
        // here, leaving nothing to debug from in production.
        reportError(
          new Error(result.message ?? 'initiate-order returned success: false'),
          { gatewayId: adapter.id, orderId: order.orderId }
        );

        onSettled({
          gatewayId: adapter.id,
          orderId: order.orderId,
          gatewayPaymentId: null,
          claimedStatus: 'failed',
        });
      })
      .catch((error) => {
        if (cancelled) return;
        onError(
          error instanceof PaymentError
            ? error
            : new PaymentError(
                PaymentErrorCode.UNKNOWN,
                'Could not start checkout.',
                {
                  gatewayId: adapter.id,
                  orderId: order.orderId,
                }
              )
        );
      });

    return () => {
      cancelled = true;
    };
  }, [adapter, order, onSettled, onError]);

  if (!adapter) {
    return <LoadingState fullPage label={`Loading ${entry.label}…`} />;
  }

  if (adapter.render) {
    const GatewayCheckout = adapter.render;
    return (
      <GatewayCheckout order={order} onSettled={onSettled} onError={onError} />
    );
  }

  return <LoadingState fullPage label={`Redirecting to ${entry.label}…`} />;
}
